import React from 'react';
import { designSystem } from '@yola/ws-ui';
import PropTypes from 'prop-types';
import GalleryPagination from 'src/js/common/components/gallery-pagination';
import GalleryGridSection from 'src/js/common/components/gallery-grid-section';
import Layout from '../layout';
import GalleryGrid from '../gallery-grid';
import CategoriesBar from '../categories-bar';
import HeaderGalleryPageContainer from '../../containers/header-gallery-page-container';
import { PREVIEW_COLUMNS_COUNT } from '../../constants/common';

const { Heading, Container, FloatingAction, Box, Stack } = designSystem;

const defaultRecommendedTemplates = [];

function GalleryLayout({
  captions,
  control,
  templates,
  canLoadMore,
  recommendedTemplates = defaultRecommendedTemplates,
  categoriesBarRef,
  galleryGridRef,
  onCategorySelect = Function.prototype,
  onTemplatePreviewClick = Function.prototype,
  onTemplateEditClick = Function.prototype,
  onBlankTemplateClick = Function.prototype,
  gridColumns = PREVIEW_COLUMNS_COUNT.twoColumns,
  handleColumnsToggle,
  onFloatingButtonClick = Function.prototype,
  onLoadMoreClick = Function.prototype,
  isNextTemplatesBatchLoading,
  isFirstTemplatesBatchLoading,
  isFloatingButtonVisible,
  isAISiteWizardEnabled = false,
}) {
  return (
    <Layout Header={HeaderGalleryPageContainer}>
      <div className="gallery-layout">
        <div>
          <Container>
            <Box breakpoints={{ xl: { paddingTop: 'spacing-xs' } }}>
              <Box
                paddingTop="spacing-xl"
                paddingBottom="spacing-xl"
                breakpoints={{
                  md: { paddingTop: 'spacing-3-xl' },
                  xl: { paddingBottom: 'spacing-2-xl' },
                }}
              >
                <Stack
                  gap="spacing-3-xs"
                  breakpoints={{
                    md: { gap: 'spacing-2-xs' },
                    xl: { gap: 'spacing-xs' },
                  }}
                >
                  <Heading type="heading-1">{captions.title}</Heading>
                  <Heading type="heading-5">{captions.subtitle}</Heading>
                </Stack>
              </Box>
            </Box>
          </Container>
          <div>
            <CategoriesBar
              ref={categoriesBarRef}
              control={control}
              onCategorySelect={onCategorySelect}
              gridColumns={gridColumns}
              onBlankTemplateClick={onBlankTemplateClick}
              handleColumnsToggle={handleColumnsToggle}
            />
            {recommendedTemplates.length > 0 ? (
              <>
                <GalleryGridSection
                  isLoading={isFirstTemplatesBatchLoading}
                  templatesLabel={captions.recommendedTemplatesLabel}
                  templates={recommendedTemplates}
                  gridColumns={gridColumns}
                  galleryGridRef={galleryGridRef}
                  isAISiteWizardEnabled={isAISiteWizardEnabled}
                  onTemplatePreviewClick={onTemplatePreviewClick}
                  onTemplateEditClick={onTemplateEditClick}
                />
                <GalleryGridSection
                  isLoading={isFirstTemplatesBatchLoading}
                  templatesLabel={captions.allTemplatesLabel}
                  templates={templates}
                  gridColumns={gridColumns}
                  isAISiteWizardEnabled={isAISiteWizardEnabled}
                  onTemplatePreviewClick={onTemplatePreviewClick}
                  onTemplateEditClick={onTemplateEditClick}
                />
              </>
            ) : (
              <div className="gallery-layout__list">
                <GalleryGrid
                  isLoading={isFirstTemplatesBatchLoading}
                  isAISiteWizardEnabled={isAISiteWizardEnabled}
                  ref={galleryGridRef}
                  templates={templates}
                  gridColumns={gridColumns}
                  onTemplatePreviewClick={onTemplatePreviewClick}
                  onTemplateEditClick={onTemplateEditClick}
                />
              </div>
            )}
          </div>
        </div>

        <GalleryPagination
          isLoading={isNextTemplatesBatchLoading}
          canLoadMore={canLoadMore}
          captions={captions}
          onLoadMoreClick={onLoadMoreClick}
        />

        {isFloatingButtonVisible && (
          <div className="gallery-layout__floating-button">
            <FloatingAction onClick={onFloatingButtonClick} iconGlyph="arrow-up" />
          </div>
        )}
      </div>
    </Layout>
  );
}

GalleryLayout.propTypes = {
  captions: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    recommendedTemplatesLabel: PropTypes.string,
    allTemplatesLabel: PropTypes.string,
    loadMoreButton: PropTypes.string,
  }).isRequired,
  control: PropTypes.shape({}).isRequired,
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      previewUrl: PropTypes.string,
    })
  ).isRequired,
  recommendedTemplates: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      slug: PropTypes.string,
      previewUrl: PropTypes.string,
    })
  ),
  categoriesBarRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
  galleryGridRef: PropTypes.shape({
    current: PropTypes.any,
  }).isRequired,
  isNextTemplatesBatchLoading: PropTypes.bool.isRequired,
  isFirstTemplatesBatchLoading: PropTypes.bool.isRequired,
  isFloatingButtonVisible: PropTypes.bool.isRequired,
  canLoadMore: PropTypes.bool.isRequired,
  gridColumns: PropTypes.oneOf(Object.values(PREVIEW_COLUMNS_COUNT)),
  onCategorySelect: PropTypes.func,
  onLoadMoreClick: PropTypes.func,
  onFloatingButtonClick: PropTypes.func,
  onTemplatePreviewClick: PropTypes.func,
  onTemplateEditClick: PropTypes.func,
  onBlankTemplateClick: PropTypes.func,
  handleColumnsToggle: PropTypes.func.isRequired,
  isAISiteWizardEnabled: PropTypes.bool,
};

export default React.memo(GalleryLayout);
